// University of Arizona: public college page (#2652, round 2), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Arizona's Common Data Set 2025-2026 (CDS_PDF_2025-26_v02_RE-PRINT.pdf,
//    linked from University Analytics and Institutional Research's Common
//    Data Set page): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table (its "unknown" column is 0), C8 test policy for Fall
//    2027, C21/C22. Quirk: the PDF's figures are drawn, not text, so they were
//    read from the rendered pages (and an OCR copy kept in evidence); the
//    men/women rows and the residency columns both sum to the totals used.
//    C21 and C22 answer no to early decision and early action.
//  - Arizona Admissions: Application Deadlines (the "Fall 2027 Start Term"
//    table), First-Year Applying & Requirements (testing, fee, essay,
//    honors) and Application Review Process. Departure: the admissions pages
//    call November 1 an Early Action deadline, while the CDS answers no to
//    early action; the admissions pages are used for the deadlines.
//  - Arizona Office of Scholarships and Financial Aid, 2026-2027 Cost of
//    Attendance for Incoming First-Year and Transfer students, on-campus
//    housing columns. The CDS G1 gives 2026-2027 tuition but also checks the
//    box saying 2026-2027 costs were not yet available, and G5 is the CDS's
//    own estimate; the financial aid office's budget is used.
//  - College Scorecard API, school id 104179 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Arizona Common Data Set 2025-2026',
        url: 'https://uair.arizona.edu/sites/default/files/2026-06/CDS_PDF_2025-26_v02_RE-PRINT.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'University of Arizona Admissions, Application Deadlines',
        url: 'https://www.arizona.edu/admissions/deadlines',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'University of Arizona Admissions, First-Year Applying & Requirements',
        url: 'https://www.arizona.edu/admissions/undergraduate/first-year',
        read: '2026-10-01',
    },
    review: {
        label: 'University of Arizona Admissions, Application Review Process',
        url: 'https://www.arizona.edu/admissions/apply/application-review',
        read: '2026-10-01',
    },
    costs: {
        label: 'University of Arizona Financial Aid, 2026-2027 Cost of Attendance for Incoming Students',
        url: 'https://financialaid.arizona.edu/cost/incoming',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?104179-University-of-Arizona',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-arizona',
    kbId: 'university_of_arizona',
    name: 'University of Arizona',
    shortName: 'Arizona',
    aliases: ['U of A'],
    location: 'Tucson, AZ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Arizona brand colors); decoration only.
    branding: {
        primary: '#AB0520',
        secondary: '#0C234B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_arizona_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 56376,
        admitted: 47080,
        enrolled: 7492,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 15165, admitted: 13461 },
            { group: 'Out-of-state', applied: 32195, admitted: 29119 },
            { group: 'International', applied: 9016, admitted: 4500 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Arizona\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'The Common Data Set answers no to both early decision and early action and gives no early counts. Arizona\'s admission pages call November 1 its Early Action deadline: applying by then brings priority consideration and a decision from mid-January.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Arizona reviews your application for every major you name and admits you to the one where it believes you are best positioned; some students are admitted to The A Center as undeclared students. The honors college asks for more:',
        rows: [
            { school: 'W.A. Franke Honors College', requirement: 'Indicate interest in honors on the application, complete the application essay, and answer the honors-specific supplemental prompt. You may add other materials, such as a writing sample, portfolio or letter of recommendation. Honors and admission decisions come together.', source: 'firstYear' },
        ],
        source: 'review',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action deadline', value: 'November 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'From January 15', binding: false },
            { plan: 'Regular Decision', deadline: 'After November 1, space available', decision: 'In batches in the spring', binding: false },
        ],
        note: 'The application opens August 1 on the Common App. Applying by November 1 brings priority consideration for admission, merit aid and the W.A. Franke Honors College; applications received later move to Regular Decision and are considered as space allows. The application fee is $50 for Arizona residents and $80 for everyone else. Arizona strongly encourages the 500-word application essay. You claim your spot by paying the enrollment fee, and Arizona encourages doing that by May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SAT and ACT scores are not required for general admission to the University of Arizona. If you send official scores, they may be used for class placement at orientation or to meet the Core Competency Requirements. During review, Arizona may ask some applicants for more information, such as test scores.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by March 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13900, 44400] },
            { item: 'Housing and food (on campus)', amounts: [17770, 17770] },
            { item: 'Books, course materials, supplies and equipment', amounts: [600, 600] },
            { item: 'Travel', amounts: [2450, 2450] },
            { item: 'Miscellaneous', amounts: [2550, 2550] },
        ],
        totals: [37270, 67770],
        note: 'Arizona\'s estimate for an incoming first-year student in on-campus housing; in-state means an Arizona resident. College fees and federal loan fees vary by program and loan, so they are not in these totals.',
        source: 'costs',
        netPrice: {
            average: 17125,
            byIncome: [
                { income: '$0 to $30,000', amount: 14327 },
                { income: '$30,001 to $48,000', amount: 14785 },
                { income: '$48,001 to $75,000', amount: 14413 },
                { income: '$75,001 to $110,000', amount: 18080 },
                { income: 'Over $110,000', amount: 24145 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Arizona\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
